import ContinueButton from "@components/ContinueButton";
import Error from "@hudson/ui/components/Error";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import { FormProvider, useForm } from "@hudson/ui/components/ReactHookForm";
import Session from "@lib/session";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import readRes from "@services/read_res";
import verifyRes from "@services/verify_res";
import { useRouter } from "next/navigation";
import { useState } from "react";
import ResNumber from "./ResNumber";
import ValidationType from "./ValidationType";
import ValidationValue from "./ValidationValue";

function ResVerify({}) {
  const router = useRouter();
  const { data: cfgData, loading: cfgLoading } = useConfig([
    "resVerifyValidationModes { field value }",
  ]);
  const { data, loading } = useContent([
    "beErrCantVerify",
    "beErrFieldRequired",
    "beErrLookupEmailFormat",
    "beReslookupHead",
    "beReslookupResnumLabel",
    "beReslookupValidationTypeLabel",
    "beReslookupValidationValueLabel",
    "beRequiredPlaceholder",
  ]);
  const formMethods = useForm({});
  const [errorMessage, setErrorMessage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  interface VerifyResData {
    resid: string;
    validationType: string;
    validationValue: string;
  }

  async function submitForm(data: VerifyResData) {
    setErrorMessage(false);
    setSubmitting(true);

    let booking;
    let result = await verifyRes(data.resid, data.validationType, data.validationValue);
    if (result) booking = await readRes({ resId: parseInt(result) });

    if (booking) {
      Session.setVerifiedBooking(booking);
      global.window.apolloClient.resetStore();
    }

    if (!result || !booking) {
      setErrorMessage(true);
      setSubmitting(false);
    } else {
      router.push(`/${window.siteId}/res_summary`);
    }
  }

  const validationType = formMethods.watch("validationType");

  return loading || cfgLoading ? (
    <ConcentricLoader className="max-w-2xl px-4 py-10 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <>
      <p className="pr-8 prose prose-md">{data?.content.beReslookupHead}</p>
      <FormProvider {...formMethods}>
        <form
          className="flex flex-col py-10 sm:py-10 gap-y-8 sm:gap-x-4"
          id="reservation-lookup"
          onSubmit={(e) => {
            e.preventDefault();
            formMethods.handleSubmit(submitForm)();
          }}
          noValidate
        >
          {errorMessage && (
            <Error className="mt-2" message={data?.content.beErrCantVerify} heavyStyle scrollTo />
          )}

          <ResNumber
            autoFocus
            errorMessage={data?.content.beErrFieldRequired}
            fieldName="resid"
            label={data?.content.beReslookupResnumLabel}
            requiredPlaceholder={data?.content.beRequiredPlaceholder}
          />

          <div className="flex flex-wrap gap-x-6 gap-y-8">
            <ValidationType
              fieldName="validationType"
              label={data?.content.beReslookupValidationTypeLabel}
              values={cfgData?.config.resVerifyValidationModes}
            />

            <ValidationValue
              key={validationType}
              errorFormatMessage={data?.content.beErrLookupEmailFormat}
              errorMessage={data?.content.beErrFieldRequired}
              fieldName="validationValue"
              label={data?.content.beReslookupValidationValueLabel}
              requiredPlaceholder={data?.content.beRequiredPlaceholder}
              validationType={validationType}
            />
          </div>

          <ContinueButton
            alignLeft
            className="mt-2 justify-self-start"
            disabled={submitting}
            loading={submitting}
          />
        </form>
      </FormProvider>
    </>
  );
}

export default ResVerify;
